import { prettyError, prettyErrorDescription } from 'constants/errors'
import { AUTH, SOCIAL_CALLBACK, SOCIAL_CHECK_USER } from 'constants/api'
import { useSession } from 'contexts/session-context'
import { signInWithCustomToken, getAuth, UserCredential } from 'firebase/auth'
import { useContext, useEffect, useState } from 'react'
import LoadingScreen from 'components/loading-screen'
import useRouter from 'hooks/use-router'
import utmServices from 'services/utm-services'
import axios, { AxiosError } from 'axios'
import MetaCAPIService from 'services/meta-capi.services'
import { createUserProperties, dlSetUserInfo, dlTrackEvent } from 'components/dataLayer/dataLayer'
import { LoaderContext } from 'contexts/loader-context'
import { UserData } from 'contexts/session-context/session-reducer'
import useRedirect from 'hooks/use-redirect'
import AllowCookies from 'components/allow-cookies'
import ErrorView from 'components/error-view'
import { getBirthdayLocal } from 'utilities/birthday'
import { Prize } from 'views/prizes/types'
import ExchangeComponent from 'components/exchange'
import CONFIGS from 'build/Config'
import textServices from 'services/text-services'
import { usePopupContext } from 'hooks/use-main-cass'
import ReferralCodeV2 from 'components/forms/v2/ReferralCode'
import toast from 'react-hot-toast'
import PopUpExchangeV2 from 'components/exchange/popUpExchange'

const Callback = () => {
  const { push, location } = useRouter()
  const [session, { setSession, setUser }] = useSession()
  const firbaseAuth = getAuth()
  const [, { redirectByLocal, getRedirectLocal }] = useRedirect()
  const { setLoad } = useContext(LoaderContext)
  const [allowCookies, setAllowCookies] = useState<{ callback: any; isAllow: boolean }>()
  const [loginError, setLoginError] = useState<number | undefined>()
  const [birthday] = useState(getBirthdayLocal())
  const [prize, setPrize] = useState<{ prize: Prize; name: string; buttonText; callback: () => void }>(null)
  const { openPopup, closePopup } = usePopupContext()
  const version = CONFIGS.versionPh || 'v1'

  const completeData = (user: UserData, session: UserCredential) => {
    setSession(session)
    setUser(user)
    const utms = localStorage.getItem('utmAuthSocial')

    const external = localStorage.getItem('toexternal')
    if (external) {
      localStorage.removeItem('toexternal')
      return window.location.assign(decodeURI(JSON.parse(external)))
    }

    if (getRedirectLocal()) return redirectByLocal()

    const availableFields = CONFIGS?.notAvailableFields
    const isComplete =
      (availableFields?.includes('nickName') ? false : !user?.nickName) ||
      !user?.birthday ||
      !user?.genre ||
      !user?.genreId
    if (!CONFIGS?.signUp?.base && isComplete && version !== 'v2') return push('/information/update?isNew=true' + `&${utms}`)

    return push(`/home?${utms}`)
  }

  const authCallback = async (data?: {
    bodyLogin: any
    session: UserCredential
    loginType: string | null
  }) => {
    setLoad(true)
    const { session, bodyLogin, loginType } = data || {}
    try {
      const accessToken = (session.user as any).accessToken
      const { data: { prize, profile: user } } = await axios.post(AUTH, bodyLogin, {
        headers: { Authorization: `Bearer ${accessToken}`, 'Ph-Api-Id': PH_API_ID ?? 1 }
      })

      dlSetUserInfo(user?.hashId ?? user?.id)
      createUserProperties(user, loginType)

      if (prize) {
        if (version === 'v2') {
          return openPopup(
            <PopUpExchangeV2
              item={prize}
              title={`¡Eso, ${user?.firstName}`}
              description={'Te damos unas oportunidades de regalo por haberte registrado con un código referido. Ahora podrás compartir tu propio código para también ganar oportunidades al invitar amigos y estar más cerca del premio del Calendario a la Final.'}
              buttons={{
                async handleButtonCommon() {
                  closePopup()
                  completeData(user, session)
                },
                async handleButtonSticker() {
                  closePopup()
                  completeData(user, session)
                },
                text: 'Continuar'
              }}
            />
          )
        }

        return setPrize({
          prize,
          name: user?.firstName,
          buttonText: '',
          callback: () => completeData(user, session)
        })
      }

      closePopup()
      return completeData(user, session)
    } catch (error) {
      if (version === 'v2') {
        if (error?.isAxiosError) {
          const { code: errorCode } = error?.response?.data ?? {}
          return toast.error(prettyErrorDescription(errorCode), { className: 'toast__error' })
        } else {
          return toast.error(prettyError(error.code), { className: 'toast__error' })
        }
      }

      const code = (error as AxiosError).response?.data.code || 99999
      setAllowCookies({ callback: authCallback, isAllow: false })
      setLoginError(code)
    } finally {
      setLoad(false)
    }
  }

  const auth = async () => {
    const params = location.hash.replace('#', '')
    const parsedParams = new URLSearchParams(params)
    const state = parsedParams.get('state') || ''
    const isStateOk = decodeURIComponent(localStorage.getItem('stateAuthSocial') || '') === state

    console.log(state)
    if (isStateOk && state !== '') {
      localStorage.removeItem('stateAuthSocial')
      const token = parsedParams.get('access_token') || ''
      const stateProvider = state.split('&')
      if (token && state && stateProvider?.length === 1) {
        try {
          const { data } = await axios.get(`${SOCIAL_CALLBACK}${stateProvider[0]}/signin?code=${token}`)
          const firebaseLogin = await signInWithCustomToken(firbaseAuth, data.firebaseAccessToken)
          const accessToken = (firebaseLogin.user as any).accessToken

          const { status } = await axios.get(`${SOCIAL_CHECK_USER}`, {
            headers: { Authorization: `Bearer ${accessToken}`, 'Ph-Api-Id': PH_API_ID ?? 1 }
          })

          const names = firebaseLogin.user?.displayName?.split(' ') || ['', '']
          const firstName = names[0]
          const lastName = names[names.length - 1]
          const utm = utmServices.utmRead()
          const lang = textServices.language
          const bodyLogin: any = { accessToken: data?.providerAccessToken, firstName, lastName, utm, lang }

          const bithdayUser = (firebaseLogin as any)?.user?.reloadUserInfo?.dateOfBirth
            ? new Date((firebaseLogin as any)?.user?.reloadUserInfo?.dateOfBirth)
            : birthday

          if (bithdayUser && !Number.isNaN(bithdayUser)) {
            bodyLogin.birthday = bithdayUser
          }

          const callbackBody = { session: firebaseLogin, loginType: stateProvider[0] }

          if (status === 204) {
            gtag('event', 'sign_up', { event_label: `SignUp - ${state}`, method: `${state}` })
            dlTrackEvent('sign_up', { method: `${state}` })
            MetaCAPIService.emitCompleteRegistration(firebaseLogin.user.email, firstName, lastName, '', window.location.href, 'Meta', MetaCAPIService.uuidv4())

            if (version === 'v2') {
              push('/')
              return openPopup(<ReferralCodeV2
                onSubmit={({ referral: code, privacyPolicy, subscribe, termsOfUse }) => {
                  authCallback({
                    ...callbackBody,
                    bodyLogin: { ...bodyLogin, termsOfUse, subscribe, code, privacyPolicy }
                  })
                }}
                isSocial
              />)
            } else {
              return setAllowCookies({
                callback: ({ termsOfUse, subscribe, code, privacyPolicy }: { termsOfUse: boolean; subscribe: boolean; code?: string, privacyPolicy?: boolean }) =>
                  authCallback({
                    ...callbackBody,
                    bodyLogin: { ...bodyLogin, termsOfUse, subscribe, code, privacyPolicy }
                  }),
                isAllow: true
              })
            }
          }

          gtag('event', 'login', {
            event_label: `Login - ${state}`,
            method: `${state}`
          })

          dlTrackEvent('login', {
            method: `${state}`
          })

          authCallback({ ...callbackBody, bodyLogin })
        } catch (error) {
          const code = (error as AxiosError).response?.data.code || 99999
          if (version === 'v2') {
            toast.error(prettyErrorDescription(code), { className: 'toast__error' })
          } else {
            setLoginError(code)
          }
        }
      } else if (token && state && session && stateProvider?.length > 1) {
        try {
          const linkAccountResponse = await axios.put(
            `${SOCIAL_CALLBACK}${stateProvider[0]}/link?code=${token}`,
            null,
            {
              headers: {
                Authorization: `Bearer ${((session as any)?.data?._tokenResponse as any)?.idToken}`,
                'Ph-Api-Id': PH_API_ID ?? 1
              }
            }
          )

          if (linkAccountResponse) {
            const prize = linkAccountResponse?.data as Prize
            if (prize)
              return setPrize({
                prize,
                name: session.user?.firstName,
                buttonText: textServices.findByText(`profile/social/exchange/description`, {
                  es: 'Vincular otras cuentas',
                  en: 'Link another account'
                }),
                callback: () => push('/profile/social')
              })

            push('/profile/social')
          }
        } catch (error) {
          const code = (error as AxiosError).response?.data.code || 99999
          setLoginError(code)
        }
      }
    } else {
      push('/')
    }
  }

  useEffect(() => {
    auth()
  }, [])

  if (prize) {
    return (
      <ExchangeComponent
        buttons={{
          handleMenu: prize?.callback,
          handleButtonCommon: prize?.callback,
          handleButtonSticker: prize?.callback,
          text: prize?.buttonText
        }}
        item={prize?.prize}
        module="login"
        name={prize?.name}
      />
    )
  }

  if (allowCookies?.isAllow) {
    return <AllowCookies onSubmit={allowCookies?.callback} />
  }

  if (loginError) {
    return <ErrorView code={loginError as number} buttonMenu={() => push('/')} onRetry={() => push('/')} />
  }

  return <LoadingScreen />
}

export default Callback
